import { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, XCircle, Info, AlertTriangle, X, Trash2, AlertCircle } from 'lucide-react';

const ToastContext = createContext(null);

const ICONS = {
  success: <CheckCircle size={18} />,
  error:   <XCircle size={18} />,
  info:    <Info size={18} />,
  warning: <AlertTriangle size={18} />,
};

const COLORS = {
  success: { bg: '#0f172a', accent: '#22c55e', icon: '#22c55e', bar: '#22c55e' },
  error:   { bg: '#0f172a', accent: '#f43f5e', icon: '#f43f5e', bar: '#f43f5e' },
  info:    { bg: '#0f172a', accent: '#38bdf8', icon: '#38bdf8', bar: '#38bdf8' },
  warning: { bg: '#0f172a', accent: '#fbbf24', icon: '#fbbf24', bar: '#fbbf24' },
};

let idCounter = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts]       = useState([]);
  const [confirms, setConfirms]   = useState([]);

  // ── Regular toast ─────────────────────────────────────────────────────────
  const addToast = useCallback((type, title, message, duration = 4000) => {
    const id = ++idCounter;
    setToasts(prev => [...prev, { id, type, title, message, duration }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // ── Confirm toast — returns a Promise<boolean> ────────────────────────────
  const confirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      const id = ++idCounter;
      setConfirms(prev => [...prev, {
        id,
        message,
        confirmLabel: options.confirmLabel || 'Delete',
        cancelLabel:  options.cancelLabel  || 'Cancel',
        danger:       options.danger !== false,
        resolve,
      }]);
    });
  }, []);

  const resolveConfirm = useCallback((id, result) => {
    setConfirms(prev => {
      const item = prev.find(c => c.id === id);
      if (item) item.resolve(result);
      return prev.filter(c => c.id !== id);
    });
  }, []);

  const toast = {
    success: (title, message, duration) => addToast('success', title, message, duration),
    error:   (title, message, duration) => addToast('error',   title, message, duration),
    info:    (title, message, duration) => addToast('info',    title, message, duration),
    warning: (title, message, duration) => addToast('warning', title, message, duration),
    confirm,
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}

      {/* ── Toast stack — top right ── */}
      <div style={{
        position: 'fixed', top: 20, right: 20,
        zIndex: 9999, display: 'flex', flexDirection: 'column',
        gap: 10, pointerEvents: 'none',
      }}>
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onRemove={removeToast} />
        ))}
      </div>

      {/* ── Confirm modals ── */}
      {confirms.map(c => (
        <ConfirmToast key={c.id} item={c} onResolve={resolveConfirm} />
      ))}
    </ToastContext.Provider>
  );
}

/* ── Regular toast item ──────────────────────────────────────────────────── */
function ToastItem({ toast: t, onRemove }) {
  const c = COLORS[t.type] || COLORS.info;
  return (
    <div style={{
      pointerEvents: 'all',
      minWidth: 300, maxWidth: 380,
      background: c.bg,
      borderRadius: 14,
      boxShadow: '0 8px 32px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.06)',
      overflow: 'hidden',
      animation: 'toastSlideIn 0.32s cubic-bezier(0.34,1.56,0.64,1)',
    }}>
      {/* Progress bar */}
      <div style={{
        height: 3,
        background: `linear-gradient(90deg, ${c.bar}, ${c.bar}88)`,
        animation: `toastBar ${t.duration}ms linear forwards`,
      }} />

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px 16px' }}>
        {/* Icon */}
        <div style={{
          width: 34, height: 34, borderRadius: 10,
          background: `${c.accent}18`, border: `1px solid ${c.accent}30`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: c.icon, flexShrink: 0,
        }}>
          {ICONS[t.type]}
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 13.5, color: '#f1f5f9', marginBottom: 3 }}>
            {t.title}
          </div>
          {t.message && (
            <div style={{ fontSize: 12.5, color: '#94a3b8', lineHeight: 1.5 }}>{t.message}</div>
          )}
        </div>

        {/* Close */}
        <button
          onClick={() => onRemove(t.id)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#475569', padding: 2, borderRadius: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#f1f5f9'}
          onMouseLeave={e => e.currentTarget.style.color = '#475569'}
        >
          <X size={15} />
        </button>
      </div>
    </div>
  );
}

/* ── Confirm toast (replaces window.confirm) ─────────────────────────────── */
function ConfirmToast({ item, onResolve }) {
  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.6)',
      backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 10000,
      animation: 'fadeIn 0.18s ease',
    }}
      onClick={() => onResolve(item.id, false)}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#0f172a',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 20,
          padding: '32px 28px',
          maxWidth: 360, width: '90%',
          textAlign: 'center',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
          animation: 'popIn 0.24s cubic-bezier(0.34,1.56,0.64,1)',
        }}
      >
        {/* Icon */}
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: item.danger ? 'rgba(244,63,94,0.12)' : 'rgba(251,191,36,0.12)',
          border: `2px solid ${item.danger ? 'rgba(244,63,94,0.25)' : 'rgba(251,191,36,0.25)'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 18px',
          color: item.danger ? '#f43f5e' : '#fbbf24',
        }}>
          {item.danger ? <Trash2 size={24} /> : <AlertCircle size={24} />}
        </div>

        {/* Title */}
        <div style={{
          fontFamily: "'Syne', sans-serif",
          fontWeight: 800, fontSize: 18,
          color: '#f1f5f9', marginBottom: 10,
        }}>
          Are you sure?
        </div>

        {/* Message */}
        <div style={{
          fontSize: 13.5, color: '#94a3b8',
          lineHeight: 1.65, marginBottom: 26,
        }}>
          {item.message}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button
            onClick={() => onResolve(item.id, false)}
            style={{
              flex: 1, padding: '10px 0',
              borderRadius: 10, border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(255,255,255,0.05)',
              color: '#94a3b8', fontWeight: 600, fontSize: 13.5,
              cursor: 'pointer', fontFamily: 'inherit',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = '#f1f5f9'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#94a3b8'; }}
          >
            {item.cancelLabel}
          </button>
          <button
            onClick={() => onResolve(item.id, true)}
            style={{
              flex: 1, padding: '10px 0',
              borderRadius: 10, border: 'none',
              background: item.danger
                ? 'linear-gradient(135deg, #f43f5e, #e11d48)'
                : 'linear-gradient(135deg, #fbbf24, #f59e0b)',
              color: '#fff', fontWeight: 700, fontSize: 13.5,
              cursor: 'pointer', fontFamily: 'inherit',
              boxShadow: item.danger
                ? '0 4px 14px rgba(244,63,94,0.4)'
                : '0 4px 14px rgba(251,191,36,0.4)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = item.danger ? '0 6px 20px rgba(244,63,94,0.5)' : '0 6px 20px rgba(251,191,36,0.5)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = item.danger ? '0 4px 14px rgba(244,63,94,0.4)' : '0 4px 14px rgba(251,191,36,0.4)'; }}
          >
            {item.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
};
